import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : d || "Failed";
};
const TONE = { pending: "text-amber-400", approved: "text-emerald-400", rejected: "text-red-400" };

export function RenewalsPanel({ api, onReload }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = async () => {
    try { setRows((await api.get("/renewals", { params: { status: filter } })).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (r) => {
    if (!window.confirm(`Approve ₹${r.amount} from ${r.hospital_name} (ref ${r.reference})?`)) return;
    try {
      const { data } = await api.post(`/renewals/${r.id}/approve`);
      toast.success(`Extended till ${data.subscription_end.slice(0, 10)}`);
      load(); onReload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (r) => {
    const reason = window.prompt("Reason for rejection");
    if (!reason) return;
    try { await api.post(`/renewals/${r.id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="platform-renewals-panel">
      <PayeeSettings api={api} />
      <div className="flex gap-2">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`renewals-filter-${s || "all"}`}
            className={`px-3 py-1 text-xs rounded-full border ${filter === s ? "border-emerald-400 text-white bg-emerald-500/10" : "border-slate-700 text-slate-400"}`}>
            {s || "all"}
          </button>
        ))}
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 text-slate-400 uppercase tracking-wider"><tr>
            {["Hospital", "Plan", "Amount", "Mode · Ref", "Paid on", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="renewals-empty">No requests</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800" data-testid={`renewal-req-${r.id}`}>
                <td className="px-3 py-2"><div className="font-medium text-slate-100">{r.hospital_name}</div><div className="text-slate-500 font-mono">{r.hospital_code} · {r.submitted_by_email}</div></td>
                <td className="px-3 py-2">{r.plan_name} · {r.cycles}× {r.billing_cycle}</td>
                <td className="px-3 py-2 font-mono text-emerald-400">₹{r.amount}</td>
                <td className="px-3 py-2"><div>{r.mode === "upi" ? "UPI" : "Bank"}</div><div className="font-mono text-slate-300">{r.reference}</div>{r.note && <div className="text-slate-500">{r.note}</div>}</td>
                <td className="px-3 py-2">{r.paid_on}</td>
                <td className={`px-3 py-2 font-semibold ${TONE[r.status]}`}>{r.status}{r.reject_reason && <div className="font-normal text-slate-500">{r.reject_reason}</div>}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.status === "pending" && (<>
                    <Button size="sm" onClick={() => approve(r)} className="h-7 bg-emerald-600 hover:bg-emerald-500 text-white mr-1" data-testid={`renewal-approve-${r.id}`}><Check className="w-3 h-3 mr-1" />Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(r)} className="h-7 bg-transparent border-red-700 text-red-300 hover:bg-red-950" data-testid={`renewal-reject-${r.id}`}><X className="w-3 h-3 mr-1" />Reject</Button>
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FIELDS = [["upi_id", "UPI ID"], ["account_name", "Account name"], ["bank_name", "Bank name"], ["account_no", "Account number"], ["ifsc", "IFSC"]];

function PayeeSettings({ api }) {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/payment-settings").then((r) => setF(r.data)).catch(() => setF({})); /* eslint-disable-next-line */ }, []);
  if (!f) return null;
  const save = async () => {
    try { await api.put("/payment-settings", f); toast.success("Payment details saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3" data-testid="payee-settings">
      <div className="text-sm font-semibold">Vyomra payment details <span className="text-slate-500 font-normal">· shown to hospital admins on their renewal page</span></div>
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <Label className="text-xs text-slate-400">{label}</Label>
            <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`payee-${k}-input`} className="bg-slate-950 border-slate-700 text-slate-100" />
          </div>
        ))}
      </div>
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <Label className="text-xs text-slate-400">Instructions</Label>
          <Input value={f.instructions || ""} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid="payee-instructions-input" className="bg-slate-950 border-slate-700 text-slate-100" />
        </div>
        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="payee-save-btn"><Save className="w-4 h-4 mr-1" />Save</Button>
      </div>
    </div>
  );
}
